import { useState } from 'react'
import {
    Button,
    DragTypes,
    DropZone as DropZoneAria,
    DropZoneProps,
    FileTrigger,
    Label,
} from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@material-ui/core'
import { FaFileCsv, FaRegFilePdf } from 'react-icons/fa6'
import { MdAttachFile, MdRemove } from 'react-icons/md'
import { PiMicrosoftExcelLogo } from 'react-icons/pi'

import { Flex } from 'app/ui/Flex'

import { getByteFormatted, mimeTypes } from 'app/utils/constants'
import { joinStyles, palette, various } from 'app/utils/theme'

/** Know if the MIME type is covered or not. */
const isMiscellaneous = (type: string) => {
    if (
        type.startsWith('image') ||
        type === mimeTypes.CSV ||
        type === mimeTypes.EXCEL ||
        type === mimeTypes.PDF
    ) {
        return false
    }

    return true
}

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        margin: various.margin,
    },

    dropZone: {
        position: 'relative',
        width: '250px',
        height: '100px',
        margin: '0 auto',
        border: `dashed 1px ${palette.grey[60]}`,
        borderRadius: various.borderRadius,
        '&[data-hovered]': {
            borderColor: palette.primary.main,
            backgroundColor: `hsla(${palette.primaryRaw.main}, 0.1)`,
        },
        '&[data-drop-target]': {
            borderColor: palette.primary.main,
            backgroundColor: `hsla(${palette.primaryRaw.main}, 0.1)`,
        },
    },

    label: {
        cursor: 'text',
        width: '250px',
        margin: '0 auto',
        marginBottom: '10px',
        padding: '0 5px',
    },

    buttonChooseFiles: {
        cursor: 'pointer',
        width: '100%',
        height: '100%',
        padding: '0',
        backgroundColor: 'unset',
        border: 'unset',
        borderRadius: various.borderRadius,
        '&[data-disabled]': {
            cursor: 'unset',
        },
    },

    fileLimit: {
        width: '100%',
        position: 'absolute',
        bottom: '0',
        zIndex: 1,
        margin: '5px',
        textAlign: 'center',
        fontStyle: 'italic',
        fontSize: '0.8em',
        color: palette.error.main,
    },

    preview: {
        flexWrap: 'wrap',
        maxWidth: '500px',
        marginTop: various.margin,
    },

    fileGroup: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        minWidth: '80px',
        margin: '5px',
        backgroundColor: `hsla(${palette.primaryRaw.main}, 0.05)`,
        borderRadius: various.borderRadius,
    },

    file: {
        maxWidth: '100%',
        height: '80px',
        borderRadius: various.borderRadius,
    },

    delete: {
        cursor: 'pointer',
        position: 'absolute',
        right: '0',
        margin: '5px',
        color: palette.text.primary,
        backgroundColor: palette.error.main,
        border: 'unset',
        borderRadius: '50%',
    },
}))

type DropZoneProperties = DropZoneProps & {
    className?: string
    label?: string
    sizeLimit?: number
    files: File[]
    setFiles: (files: File[]) => void
    acceptedFileTypes?: string[]
    allowsMultiple?: boolean
    required?: boolean
}

export const DropZone = ({
    className,
    label,
    sizeLimit,
    files,
    setFiles,
    acceptedFileTypes,
    allowsMultiple,
    required,
    isDisabled,
    ...properties
}: Readonly<DropZoneProperties>): JSX.Element => {
    const classes = useStyles()
    const { t } = useTranslation()

    const [fileLimitExceeded, setFileLimitExceeded] = useState(false)

    /** Manage the drop operation depending on the accepted MIME types. */
    const handleDropOperation = (types: DragTypes) => {
        // @ts-expect-error Wait type
        // eslint-disable-next-line unicorn/no-useless-spread
        for (const type of [...types.types]) {
            const isTypeInclude = acceptedFileTypes.includes(type)

            if (!isTypeInclude) {
                return 'cancel'
            }
        }

        return 'copy'
    }

    /** Manage the dropped files. */
    // @ts-expect-error Wait type
    const onDrop = async (event: DropEvent) => {
        setFileLimitExceeded(false)

        const filesUpdated = files

        for (const file of event.items) {
            if (!allowsMultiple && filesUpdated.length === 1) {
                // eslint-disable-next-line no-continue
                continue
            }

            // eslint-disable-next-line no-await-in-loop
            const fileLoaded = await file.getFile()

            if (sizeLimit && fileLoaded.size > sizeLimit) {
                setFileLimitExceeded(true)

                // eslint-disable-next-line no-continue
                continue
            }

            filesUpdated.push(fileLoaded)
        }

        setFiles(filesUpdated)
    }

    /** Manage the selected files. */
    const handleFileTrigger = (filesTriggered: FileList) => {
        setFileLimitExceeded(false)

        const filesUpdated = files

        if (!allowsMultiple && filesUpdated.length === 1) {
            return
        }

        for (const file of filesTriggered) {
            // Check file size
            if (sizeLimit && file.size > sizeLimit) {
                setFileLimitExceeded(true)

                // eslint-disable-next-line no-continue
                continue
            }

            // Check file type
            if (acceptedFileTypes.includes(file.type)) {
                filesUpdated.push(file)
            }
        }

        setFiles(filesUpdated)
    }

    /** Delete the file depending on the file index. */
    const handleDelete = (index: number) => {
        setFileLimitExceeded(false)

        const newFiles = structuredClone(files)

        newFiles.splice(index, 1)

        setFiles(newFiles)
    }

    return (
        <div className={classes.root}>
            {/* Label */}
            {!!label && (
                <Label className={classes.label}>
                    {required ? `${label} *` : label}
                </Label>
            )}

            {/* Drop zone */}
            <DropZoneAria
                className={joinStyles(classes.dropZone, className)}
                getDropOperation={(types) => handleDropOperation(types)}
                onDrop={async (event) => {
                    await onDrop(event)
                }}
                isDisabled={isDisabled}
                {...properties}
            >
                {/* Button to open the file selection */}
                <FileTrigger
                    acceptedFileTypes={acceptedFileTypes}
                    allowsMultiple={allowsMultiple}
                    onSelect={handleFileTrigger}
                >
                    <Button
                        className={classes.buttonChooseFiles}
                        isDisabled={isDisabled}
                    >
                        <div>{t('dropFileHere')}</div>

                        {!!sizeLimit && (
                            <div>{`(< ${getByteFormatted(sizeLimit)})`}</div>
                        )}
                    </Button>
                </FileTrigger>

                {/* File size error */}
                {!!fileLimitExceeded && (
                    <div className={classes.fileLimit}>
                        {t('fileSizeError')}
                    </div>
                )}
            </DropZoneAria>

            {/* Previews */}
            <Flex className={classes.preview}>
                {files.map((file, index) => (
                    <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        className={classes.fileGroup}
                    >
                        {/* Image */}
                        {file.type.startsWith('image') && (
                            <img
                                className={classes.file}
                                src={URL.createObjectURL(file)}
                                alt='Preview'
                            />
                        )}

                        {/* CSV */}
                        {file.type === mimeTypes.CSV && (
                            <FaFileCsv
                                className={classes.file}
                                size='4rem'
                            />
                        )}

                        {/* Excel */}
                        {file.type === mimeTypes.EXCEL && (
                            <PiMicrosoftExcelLogo
                                className={classes.file}
                                size='4rem'
                            />
                        )}

                        {/* PDF */}
                        {file.type === mimeTypes.PDF && (
                            <FaRegFilePdf
                                className={classes.file}
                                size='4rem'
                            />
                        )}

                        {/* Other */}
                        {isMiscellaneous(file.type) && (
                            <MdAttachFile
                                className={classes.file}
                                size='4rem'
                            />
                        )}

                        {/* Remove button */}
                        {!isDisabled && (
                            <Button
                                className={classes.delete}
                                onPress={() => handleDelete(index)}
                            >
                                <MdRemove />
                            </Button>
                        )}
                    </div>
                ))}
            </Flex>
        </div>
    )
}
